import React, { useEffect, useState, useCallback } from "react";
import { Plus, Power, Pencil, Check, X } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { useAuth } from "../contexts/AuthContext";

const FIELDS = [
    { key: "company_name", label: "Company name" },
    { key: "company_address1", label: "Address line 1" },
    { key: "company_address2", label: "Address line 2 (City, State ZIP)" },
    { key: "company_phone", label: "Phone" },
    { key: "company_email", label: "Email" },
    { key: "company_web", label: "Website" },
    { key: "default_eta_days", label: "Default ETA days (PO date + N)", type: "number" },
    { key: "default_overrun_pct", label: "Default overrun %" },
    { key: "default_underrun_pct", label: "Default underrun %" },
];

export default function Settings() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [data, setData] = useState(null);
    const [draft, setDraft] = useState({});
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        api.get("/settings").then((r) => {
            setData(r.data);
            setDraft(r.data);
        });
    }, []);

    const change = (k, v) => {
        setDraft((d) => ({ ...d, [k]: v }));
        setSaved(false);
    };

    const save = async () => {
        setBusy(true);
        setError("");
        try {
            const payload = {};
            for (const f of FIELDS) {
                const v = draft[f.key];
                if (v === "" || v == null) continue;
                payload[f.key] = f.type === "number" ? Number(v) : String(v);
            }
            const { data: updated } = await api.patch("/settings", payload);
            setData(updated);
            setDraft(updated);
            setSaved(true);
        } catch (err) {
            setError(formatApiError(err?.response?.data?.detail) || err.message);
        } finally {
            setBusy(false);
        }
    };

    if (!data) {
        return (
            <div data-testid="settings-page">
                <PageHeader title="Settings" subtitle="Loading…" />
            </div>
        );
    }

    return (
        <div data-testid="settings-page">
            <PageHeader
                title="Settings"
                subtitle={
                    isAdmin
                        ? "Company info, default ETA, default tolerances. Changes are audit-logged."
                        : "Company info (read-only — admin access required to edit)."
                }
            />
            <div className="bg-white border border-gray-200 rounded-sm p-4 max-w-2xl">
                {FIELDS.map((f) => (
                    <div key={f.key} className="mb-3">
                        <label className="block text-[11px] uppercase tracking-wider font-semibold text-gray-600 mb-1">
                            {f.label}
                        </label>
                        <input
                            type={f.type || "text"}
                            value={draft[f.key] ?? ""}
                            onChange={(e) => change(f.key, e.target.value)}
                            disabled={!isAdmin}
                            className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm disabled:bg-gray-50 disabled:text-gray-500"
                            data-testid={`settings-${f.key}`}
                        />
                    </div>
                ))}

                {error && (
                    <div className="text-sm border border-red-200 bg-red-50 text-red-700 rounded-sm px-3 py-2 mb-3">
                        {error}
                    </div>
                )}
                {saved && !error && (
                    <div className="text-sm border border-green-200 bg-green-50 text-green-800 rounded-sm px-3 py-2 mb-3">
                        Saved.
                    </div>
                )}

                {isAdmin && (
                    <div className="flex justify-end gap-2 pt-3 border-t border-gray-200">
                        <button
                            onClick={() => {
                                setDraft(data);
                                setSaved(false);
                            }}
                            className="odr-btn rounded-sm border border-gray-300 px-4 text-sm text-gray-700 hover:bg-gray-50"
                            data-testid="settings-reset"
                        >
                            Reset
                        </button>
                        <button
                            onClick={save}
                            disabled={busy}
                            className="odr-btn rounded-sm bg-[var(--odr-red)] hover:bg-[var(--odr-red-hover)] text-white text-sm font-semibold px-4 disabled:opacity-60"
                            data-testid="settings-save"
                        >
                            {busy ? "Saving…" : "Save changes"}
                        </button>
                    </div>
                )}
            </div>

            {/* V1#3 — Product dropdown option managers */}
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 max-w-5xl">
                <BoxOptionManager kind="box-styles" title="Box Styles" canEdit={isAdmin} />
                <BoxOptionManager kind="joints" title="Joints" canEdit={isAdmin} />
                <BoxOptionManager kind="tests" title="Tests" canEdit={isAdmin} />
            </div>

            {isAdmin && <FreshLaunchPanel />}
        </div>
    );
}

function BoxOptionManager({ kind, title, canEdit }) {
    const [items, setItems] = useState([]);
    const [adding, setAdding] = useState(false);
    const [newName, setNewName] = useState("");
    const [editingId, setEditingId] = useState(null);
    const [editingName, setEditingName] = useState("");
    const [err, setErr] = useState("");

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/product-options/${kind}`);
            setItems(data);
            setErr("");
        } catch (e) {
            setErr(formatApiError(e?.response?.data?.detail) || e.message);
        }
    }, [kind]);

    useEffect(() => {
        load();
    }, [load]);

    const create = async () => {
        if (!newName.trim()) return;
        try {
            await api.post(`/product-options/${kind}`, { name: newName.trim() });
            setNewName("");
            setAdding(false);
            await load();
        } catch (e) {
            setErr(formatApiError(e?.response?.data?.detail) || e.message);
        }
    };

    const saveEdit = async (id) => {
        if (!editingName.trim()) return;
        try {
            await api.patch(`/product-options/${kind}/${id}`, { name: editingName.trim() });
            setEditingId(null);
            await load();
        } catch (e) {
            setErr(formatApiError(e?.response?.data?.detail) || e.message);
        }
    };

    const toggle = async (item) => {
        try {
            await api.patch(`/product-options/${kind}/${item.id}`, {
                active: !item.active,
            });
            await load();
        } catch (e) {
            setErr(formatApiError(e?.response?.data?.detail) || e.message);
        }
    };

    return (
        <div
            className="bg-white border border-gray-200 rounded-sm p-3"
            data-testid={`box-option-manager-${kind}`}
        >
            <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
                {canEdit && !adding && (
                    <button
                        onClick={() => setAdding(true)}
                        className="odr-btn inline-flex items-center gap-1 rounded-sm border border-gray-300 px-2 py-0.5 text-[11px] text-gray-700 hover:bg-gray-50"
                        data-testid={`add-${kind}`}
                    >
                        <Plus className="w-3 h-3" /> Add
                    </button>
                )}
            </div>
            {err && (
                <div className="text-xs border border-red-200 bg-red-50 text-red-700 rounded-sm px-2 py-1 mb-2">
                    {err}
                </div>
            )}
            {adding && (
                <div className="flex gap-1 mb-2">
                    <input
                        autoFocus
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && create()}
                        placeholder="New option name"
                        className="odr-input flex-1 rounded-sm border border-gray-300 px-2 text-xs"
                        data-testid={`add-${kind}-input`}
                    />
                    <button
                        onClick={create}
                        className="odr-btn rounded-sm bg-[var(--odr-red)] hover:bg-[var(--odr-red-hover)] text-white text-xs font-semibold px-2"
                        data-testid={`add-${kind}-save`}
                    >
                        <Check className="w-3 h-3" />
                    </button>
                    <button
                        onClick={() => {
                            setAdding(false);
                            setNewName("");
                        }}
                        className="odr-btn rounded-sm border border-gray-300 px-2 text-xs text-gray-700 hover:bg-gray-50"
                    >
                        <X className="w-3 h-3" />
                    </button>
                </div>
            )}
            <ul className="space-y-1">
                {items.length === 0 && (
                    <li className="text-xs text-gray-400 italic">No options yet.</li>
                )}
                {items.map((item) => (
                    <li
                        key={item.id}
                        className={`flex items-center justify-between gap-2 px-2 py-1 rounded-sm text-sm ${
                            item.active ? "" : "opacity-50"
                        }`}
                        data-testid={`option-${kind}-${item.id}`}
                    >
                        {editingId === item.id ? (
                            <>
                                <input
                                    autoFocus
                                    value={editingName}
                                    onChange={(e) => setEditingName(e.target.value)}
                                    onKeyDown={(e) =>
                                        e.key === "Enter" && saveEdit(item.id)
                                    }
                                    className="odr-input flex-1 rounded-sm border border-gray-300 px-2 text-xs"
                                />
                                <button
                                    onClick={() => saveEdit(item.id)}
                                    className="text-green-700 hover:text-green-900"
                                >
                                    <Check className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => setEditingId(null)}
                                    className="text-gray-500 hover:text-gray-700"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </>
                        ) : (
                            <>
                                <span className="flex-1">{item.name}</span>
                                {canEdit && (
                                    <>
                                        <button
                                            onClick={() => {
                                                setEditingId(item.id);
                                                setEditingName(item.name);
                                            }}
                                            className="text-gray-500 hover:text-gray-800"
                                            title="Rename"
                                            data-testid={`edit-${kind}-${item.id}`}
                                        >
                                            <Pencil className="w-3.5 h-3.5" />
                                        </button>
                                        <button
                                            onClick={() => toggle(item)}
                                            className={
                                                item.active
                                                    ? "text-gray-500 hover:text-red-700"
                                                    : "text-red-600 hover:text-red-900"
                                            }
                                            title={item.active ? "Deactivate" : "Reactivate"}
                                            data-testid={`toggle-${kind}-${item.id}`}
                                        >
                                            <Power className="w-3.5 h-3.5" />
                                        </button>
                                    </>
                                )}
                            </>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );
}

/** Danger zone — full operational wipe for production launch.
 *  Admin-only. Requires typing "FRESH-LAUNCH" exactly + password + reason. */
function FreshLaunchPanel() {
    const [open, setOpen] = useState(false);
    const [confirm, setConfirm] = useState("");
    const [password, setPassword] = useState("");
    const [reason, setReason] = useState("");
    const [wipeCustomers, setWipeCustomers] = useState(false);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");

    const canFire =
        confirm.trim() === "FRESH-LAUNCH"
        && password.length > 0
        && reason.trim().length >= 5;

    const fire = async () => {
        setBusy(true);
        setError("");
        try {
            const { data } = await api.post("/admin/reset-fresh-launch", {
                confirm: "FRESH-LAUNCH",
                password,
                reason: reason.trim(),
                wipe_customers: wipeCustomers,
            });
            setResult(data);
            setConfirm("");
            setPassword("");
            setReason("");
            setWipeCustomers(false);
        } catch (err) {
            setError(formatApiError(err?.response?.data?.detail) || err.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="mt-10 border-2 border-red-300 rounded-sm bg-red-50/40 p-5">
            <h3 className="text-sm uppercase tracking-wider font-bold text-red-800 mb-2">
                Danger Zone — Fresh Launch
            </h3>
            <p className="text-sm text-gray-700 mb-3">
                Wipe every operational record — orders, RPs, packing lists, load
                tags, documents, products, joints, tests, box styles, audit logs,
                pallets, GridFS storage — and reset the global numbering counters.
                Also deletes every non-root user (root admin <span className="odr-mono">operalta</span>{" "}
                is preserved). <strong>Customers are preserved by default.</strong>{" "}
                Tick &ldquo;Also delete all customers&rdquo; below to wipe them too. Run this
                only when preparing a fresh deployment.
            </p>
            {!open && (
                <button
                    type="button"
                    onClick={() => setOpen(true)}
                    className="odr-btn rounded-sm bg-red-700 hover:bg-red-800 text-white text-sm font-semibold px-4"
                    data-testid="open-fresh-launch"
                >
                    Open Fresh Launch Reset
                </button>
            )}
            {open && (
                <div className="space-y-3 mt-2">
                    <label className="block text-xs font-semibold uppercase tracking-wider text-red-800">
                        Type <span className="odr-mono">FRESH-LAUNCH</span> to confirm
                    </label>
                    <input
                        type="text"
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                        className="odr-input w-64 rounded-sm border border-red-300 px-3 text-sm odr-mono"
                        data-testid="fresh-launch-confirm-input"
                        placeholder="FRESH-LAUNCH"
                    />

                    <label className="block text-xs font-semibold uppercase tracking-wider text-red-800">
                        Admin password
                    </label>
                    <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="odr-input w-64 rounded-sm border border-red-300 px-3 text-sm odr-mono"
                        data-testid="fresh-launch-password"
                        placeholder="Your admin password"
                    />

                    <label className="block text-xs font-semibold uppercase tracking-wider text-red-800">
                        Reason (≥ 5 chars, recorded in audit)
                    </label>
                    <textarea
                        rows={2}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        className="odr-input w-full max-w-md rounded-sm border border-red-300 px-3 py-2 text-sm"
                        data-testid="fresh-launch-reason"
                        placeholder="e.g. Preparing prod cutover after pilot phase"
                    />

                    <label className="flex items-start gap-2 pt-2 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={wipeCustomers}
                            onChange={(e) => setWipeCustomers(e.target.checked)}
                            className="mt-0.5"
                            data-testid="fresh-launch-wipe-customers"
                        />
                        <span className="text-sm text-red-900">
                            <strong>Also delete ALL customers</strong> (default: preserved).{" "}
                            Tick this only if you want a truly empty baseline — every
                            customer row, including the ones in your real list, will be
                            removed. You&rsquo;ll need to re-import them afterward.
                        </span>
                    </label>

                    {error && (
                        <div className="text-sm border border-red-200 bg-red-50 text-red-700 rounded-sm px-3 py-2">
                            {error}
                        </div>
                    )}
                    {result && (
                        <div className="text-xs border border-green-200 bg-green-50 text-green-800 rounded-sm px-3 py-2 odr-mono whitespace-pre-wrap">
                            Reset complete.
                            {"\n"}Cleared: {JSON.stringify(result.cleared)}
                            {"\n"}Preserved: {JSON.stringify(result.preserved)}
                        </div>
                    )}
                    <div className="flex gap-2">
                        <button
                            type="button"
                            disabled={busy}
                            onClick={() => {
                                setOpen(false);
                                setConfirm("");
                                setPassword("");
                                setReason("");
                                setError("");
                                setResult(null);
                            }}
                            className="odr-btn rounded-sm border border-gray-300 px-4 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            data-testid="fresh-launch-cancel"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={fire}
                            disabled={!canFire || busy}
                            className="odr-btn rounded-sm bg-red-700 hover:bg-red-800 text-white text-sm font-semibold px-4 disabled:opacity-50"
                            data-testid="fresh-launch-confirm"
                        >
                            {busy ? "Resetting…" : "Permanently Wipe Operational Data"}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
